import React, { useMemo, useState } from "react";
import { Plus, Users } from "lucide-react";
import type { AuthorizationUser, RoleResource } from "../../../features/rbac/types";
import { EmptyState } from "../shared/EmptyState";
import { FormModal, LabeledField, SelectInput, TextInput } from "../shared/FormModal";
import { SearchInput } from "../shared/SearchInput";
import { SectionCard } from "../shared/SectionCard";
import { StatusPill } from "../shared/StatusPill";
import { useRbacAction } from "../hooks/useRbacAction";

interface UsersTabProps {
  users: AuthorizationUser[];
  roleResources: RoleResource[];
  onCreate: (payload: { roleResourceId: number; name: string; email: string; password: string }) => void;
  onUpdate: (payload: { userId: number; roleResourceId: number }) => void;
}

interface CreateUserForm {
  roleResourceId: number | "";
  name: string;
  email: string;
  password: string;
}

const emptyCreateForm: CreateUserForm = { roleResourceId: "", name: "", email: "", password: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const UsersTab: React.FC<UsersTabProps> = ({ users, roleResources, onCreate, onUpdate }) => {
  const { matches, isBusy } = useRbacAction();

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>(emptyCreateForm);

  const [reassignTarget, setReassignTarget] = useState<AuthorizationUser | null>(null);
  const [reassignTo, setReassignTo] = useState<number | "">("");

  const roleResourceMap = useMemo(
    () => new Map(roleResources.map((rr) => [rr.roleResourceId, rr])),
    [roleResources]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const rrLabel = roleResourceMap.get(u.roleResourceId)?.role ?? "";
      return (
        (u.name ?? "").toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        rrLabel.toLowerCase().includes(q) ||
        String(u.userId).includes(q)
      );
    });
  }, [users, search, roleResourceMap]);

  const openCreate = () => {
    setCreateForm({
      roleResourceId: roleResources[0]?.roleResourceId ?? "",
      name: "",
      email: "",
      password: "",
    });
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateForm(emptyCreateForm);
  };

  const trimmed = {
    name: createForm.name.trim(),
    email: createForm.email.trim(),
  };
  const emailValid = EMAIL_RE.test(trimmed.email);
  const passwordValid = createForm.password.length >= 6;
  const createDisabled =
    createForm.roleResourceId === "" ||
    !trimmed.name ||
    !trimmed.email ||
    !emailValid ||
    !passwordValid;

  const handleCreateSubmit = () => {
    if (createDisabled) return;
    onCreate({
      roleResourceId: createForm.roleResourceId as number,
      name: trimmed.name,
      email: trimmed.email,
      password: createForm.password,
    });
    closeCreate();
  };

  const openReassign = (user: AuthorizationUser) => {
    setReassignTarget(user);
    setReassignTo(user.roleResourceId || roleResources[0]?.roleResourceId || "");
  };

  const closeReassign = () => {
    setReassignTarget(null);
    setReassignTo("");
  };

  const reassignDisabled =
    !reassignTarget || reassignTo === "" || reassignTo === reassignTarget.roleResourceId;

  const handleReassignSubmit = () => {
    if (!reassignTarget || reassignDisabled) return;
    onUpdate({ userId: reassignTarget.userId, roleResourceId: reassignTo as number });
    closeReassign();
  };

  return (
    <SectionCard
      title="Users"
      description="Users in your authenticated role-resource tree. Reassign users between role-resources or invite new ones."
      actions={
        <>
          <SearchInput value={search} onChange={setSearch} placeholder="Search users" />
          <button
            type="button"
            onClick={openCreate}
            disabled={isBusy || roleResources.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            New user
          </button>
        </>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title={search ? "No users match your search" : "No users in your tree"}
          description={
            search
              ? "Try a different search term."
              : "Create your first user under one of your role-resources."
          }
          action={
            !search && roleResources.length > 0 && (
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90"
              >
                <Plus className="h-4 w-4" />
                New user
              </button>
            )
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-cardborder bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2">User ID</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Role-resource</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => {
                const rr = roleResourceMap.get(user.roleResourceId);
                return (
                  <tr
                    key={user.userId}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{user.userId}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {user.name?.trim() ? user.name : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{user.email}</td>
                    <td className="px-4 py-3">
                      {rr ? (
                        <StatusPill variant="info">
                          {rr.role} (#{rr.roleResourceId})
                        </StatusPill>
                      ) : (
                        <StatusPill variant="warning">
                          unassigned #{user.roleResourceId}
                        </StatusPill>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openReassign(user)}
                        disabled={isBusy}
                        className="rounded-md border border-cardborder bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Reassign role
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <FormModal
        open={createOpen}
        title="Invite new user"
        onClose={closeCreate}
        onSubmit={handleCreateSubmit}
        submitting={matches("createAuthorizationUser")}
        submitDisabled={createDisabled}
        submitLabel="Create user"
      >
        <LabeledField label="Assign to role-resource" required>
          <SelectInput
            value={createForm.roleResourceId}
            onChange={(e) =>
              setCreateForm((f) => ({
                ...f,
                roleResourceId: e.target.value === "" ? "" : Number(e.target.value),
              }))
            }
          >
            <option value="" disabled>
              Select role-resource
            </option>
            {roleResources.map((rr) => (
              <option key={rr.roleResourceId} value={rr.roleResourceId}>
                {rr.role} (#{rr.roleResourceId})
              </option>
            ))}
          </SelectInput>
        </LabeledField>
        <LabeledField label="Name" required>
          <TextInput
            value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Full name"
            autoFocus
          />
        </LabeledField>
        <LabeledField
          label="Email"
          required
          error={createForm.email && !emailValid ? "Enter a valid email address." : undefined}
        >
          <TextInput
            value={createForm.email}
            onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="name@example.com"
            type="email"
            invalid={!!createForm.email && !emailValid}
          />
        </LabeledField>
        <LabeledField
          label="Password"
          required
          hint="At least 6 characters."
          error={
            createForm.password && !passwordValid ? "Password must be at least 6 characters." : undefined
          }
        >
          <TextInput
            value={createForm.password}
            onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Choose a temporary password"
            type="password"
            invalid={!!createForm.password && !passwordValid}
          />
        </LabeledField>
      </FormModal>

      <FormModal
        open={reassignTarget != null}
        title={`Reassign user · ${reassignTarget?.email ?? ""}`}
        onClose={closeReassign}
        onSubmit={handleReassignSubmit}
        submitting={
          reassignTarget ? matches("updateAuthorizationUser", reassignTarget.userId) : false
        }
        submitDisabled={reassignDisabled}
        submitLabel="Save assignment"
      >
        <LabeledField label="New role-resource" required>
          <SelectInput
            value={reassignTo}
            onChange={(e) =>
              setReassignTo(e.target.value === "" ? "" : Number(e.target.value))
            }
          >
            <option value="" disabled>
              Select role-resource
            </option>
            {roleResources.map((rr) => (
              <option key={rr.roleResourceId} value={rr.roleResourceId}>
                {rr.role} (#{rr.roleResourceId})
              </option>
            ))}
          </SelectInput>
        </LabeledField>
      </FormModal>
    </SectionCard>
  );
};
